import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { RhythmPatternPlayer, SON_CLAVE, RUMBA_CLAVE, BOSSA_NOVA } from "@/components/mt/RhythmPatternPlayer";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory18() {
  const { lesson, course, accent } = findLesson("music-theory", "18");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT18-Claves-Animated.mp4"
        caption="Son · Rumba · Bossa claves on a 2-bar grid"
        hint="≈ 46 sec · watch first"
      />
      <Chapter number="18" eyebrow="Music Theory · Chapter Eighteen" title="From the tresillo to the clave">
        <Prose>
          <p>
            In the previous chapter we built the <em>tresillo</em> — three uneven hits across
            a bar of four, the spine of so much pop and Latin music. The tresillo on its own
            is already a complete rhythmic sentence. But it can also be a foundation: the
            opening half of a longer two-bar phrase that musicians have been using for
            centuries to organise dance music. That longer phrase is called a{" "}
            <strong>clave</strong>.
          </p>
          <p>
            The word <em>clave</em> simply means "stick" — in fact, if you search Amazon for
            claves, what comes up is a pair of short hardwood sticks that you strike together.
            The names you'll meet in this chapter — <em>son</em>, <em>rumba</em>,{" "}
            <em>bossa</em> — are the names of the musical styles each pattern belongs to:
            Cuban son, Afro-Cuban rumba, Brazilian bossa nova. What matters for our purposes is that
            each clave is a fixed two-bar rhythmic pattern, used as the heartbeat of a Latin
            ensemble. Junior players hold the clave; senior players weave montunos and
            melodies on top.
          </p>
          <MTQuote>
            The clave is sort of like the pulse for a lot of musicians. Maybe a junior
            musician would be playing the clave, and the senior musicians in an ensemble,
            especially in a Latin music ensemble, will be doing all sorts of other things
            over the clave, like montunos and other such phrases.
          </MTQuote>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Anatomy of a clave" title="Three hits, then two — across two bars">
        <Prose>
          <p>
            Every clave you'll meet here is a <strong>two-bar phrase</strong>. The first bar
            carries <strong>three</strong> hits — these are the tresillo or a close cousin of
            it. The second bar carries <strong>two</strong> hits, much simpler, almost a
            release. Together they form what musicians call a "3-2" clave. Flip the order —
            two hits first, then three — and you have a "2-3" clave. The same notes, but the
            feel changes completely depending on which side starts the phrase.
          </p>
          <p>
            Three of these patterns dominate Latin music: the <strong>son clave</strong>, the{" "}
            <strong>rumba clave</strong>, and the <strong>bossa nova clave</strong>. They are
            very nearly identical — each one differs from the others by a single displaced
            hit. Tiny on paper. Huge in feel.
          </p>
        </Prose>

        <Glossary>
          <Term name="Clave" def="A fixed two-bar rhythmic pattern used as the timekeeper of a Latin ensemble. Spanish for 'stick'." />
          <Term name="3-2 clave" def="The phrase where the busy three-hit bar comes first, the simpler two-hit bar second. The default direction." />
          <Term name="2-3 clave" def="The same two bars in reverse order — relaxed first, tense second. Inspires different melodies." />
          <Term name="Tresillo" def="The three-hit pattern that opens most claves: 1 — and-of-2 — 4. The Latin spine of the phrase." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="The son clave" title="The first clave you should learn">
        <Prose>
          <p>
            The son clave is a cornerstone of Afro-Cuban music, and it turns up in pop too:
            the "Bo Diddley beat" and the rhythm under George Michael's <em>Faith</em> are close
            relatives of it.
          </p>
          <p>
            Build it by adding two hits to a tresillo. Count two bars of eighth notes:{" "}
            <span className="font-mono">1 and 2 and 3 and 4 and · 1 and 2 and 3 and 4 and</span>.
            Strike on the <strong>1</strong>, the <strong>and-of-2</strong>, and the{" "}
            <strong>4</strong> of the first bar — that's your tresillo. Then in the second
            bar, strike only on the <strong>2</strong> and the <strong>3</strong>, and rest
            everything else. That's your son clave.
          </p>
        </Prose>

        <TheoryBox label="Son clave (3-2)">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>1 &nbsp;·&nbsp; &amp;-of-2 &nbsp;·&nbsp; 4 &nbsp;&nbsp;|&nbsp;&nbsp; 2 &nbsp;·&nbsp; 3</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            Bar 1: three hits — 1, the and-of-2, and 4. Bar 2: two hits — 2 and 3. Loop.
          </div>
        </TheoryBox>

        <Prose>
          <p>
            You can count it slow (eighth notes) or fast (sixteenth notes). The notes don't
            change — only the <em>vibe</em> does. The eighth-note feel is loungy and reflective.
            The sixteenth-note feel is salsa: a relentless, dancing pulse where everything
            else in the band hangs off this skeleton.
          </p>
        </Prose>

        <NoteImage
          src="/notes/music-theory/finals/18a-son-clave.png"
          caption="Jason's son-clave note: the 5-stroke skeleton sketched against both an eighth-note count (slow) and a sixteenth-note count (fast). Bar 1: 1, &-of-2, 4. Bar 2: 2, 3."
        />
      </Chapter>

      <Chapter eyebrow="The rumba clave" title="One sixteenth later — and a whole new groove">
        <Prose>
          <p>
            The rumba clave is the son clave with <strong>one</strong> note moved. Take the
            third hit of the first bar — the one that sat on beat 4 in the son — and push
            it later, onto the <strong>and</strong> of beat 4. That's it. That
            single displacement transforms the whole pattern: it tugs the dancer harder, leans
            more drunkenly into the second bar, and feels distinctly Afro-Cuban rumba rather
            than Cuban son.
          </p>
        </Prose>

        <TheoryBox label="Rumba clave (3-2)">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>1 &nbsp;·&nbsp; &amp;-of-2 &nbsp;·&nbsp; &amp;-of-4 &nbsp;&nbsp;|&nbsp;&nbsp; 2 &nbsp;·&nbsp; 3</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            Only bar 1's third hit moves — off beat 4, onto the and-of-4. Bar 2 stays the same.
          </div>
        </TheoryBox>

        <MTQuote>
          There has to be groove. You may have a melody, you may have a great chord
          progression, but you definitely need a groove.
        </MTQuote>

        <NoteImage
          src="/notes/music-theory/finals/18b-rumba-clave.png"
          caption="Jason's rumba-clave note. Same bar 2 as son. Bar 1's third stroke pushed late — &-of-4 instead of beat 4. One displacement; brand-new groove."
        />
      </Chapter>

      <Chapter eyebrow="The bossa clave" title="The modern-pop clave hiding in plain sight">
        <Prose>
          <p>
            The bossa nova clave was born in Brazil and travelled the world via Jobim and
            Gilberto. But once you know its shape, you start hearing it everywhere — in
            Coldplay, in Chainsmokers, in EDM drops, in lo-fi loops. It's almost the default
            grid for modern pop production.
          </p>
          <p>
            Build it from the son clave again — and again displace just one note. This time
            the second-bar hit moves: the second clap of bar two slides from the{" "}
            <strong>3</strong> over to the <strong>and of 3</strong> — one eighth later,
            exactly the same size of nudge that turned son into rumba, just applied to a
            different stroke. Tiny shift, very different feel — the bossa lands smoother,
            more even, less syncopated than its Cuban cousins.
          </p>
        </Prose>

        <TheoryBox label="Bossa nova clave (3-2)">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>1 &nbsp;·&nbsp; &amp;-of-2 &nbsp;·&nbsp; 4 &nbsp;&nbsp;|&nbsp;&nbsp; 2 &nbsp;·&nbsp; &amp;-of-3</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            Bar 1 = tresillo. Bar 2's second hit slides off 3 and onto the and-of-3.
          </div>
        </TheoryBox>

        <NoteImage
          src="/notes/music-theory/finals/18c-bossa-clave.png"
          caption="Jason's bossa-clave note. Bar 1: 1, &-of-2, 4 (the tresillo). Bar 2: 2, and-of-3. Brazil's quieter clave — softer than son, but no less precise."
        />
      </Chapter>

      <Chapter eyebrow="Hear them side by side" title="Three patterns · one player">
        <Prose>
          <p>
            Switch between the three claves in the player below. Listen for what stays the
            same and what shifts. The tresillo bar opens all three. Only one hit per pattern
            distinguishes son from rumba, son from bossa. Yet the dance, the geography, and
            the genre that grow on top of each one are unmistakable.
          </p>
        </Prose>

        <RhythmPatternPlayer patterns={[SON_CLAVE, RUMBA_CLAVE, BOSSA_NOVA]} />

        <Prose>
          <p>
            Try counting along: slow first, in eighths — <span className="font-mono">1 and 2
            and 3 and 4 and</span>. Then double it, sixteenth notes —{" "}
            <span className="font-mono">1 e and a 2 e and a 3 e and a 4 e and a</span>. The
            grid never changes. Only your relationship to it does.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="3-2 vs 2-3" title="The same notes — flipped">
        <Prose>
          <p>
            Every clave can also be played in reverse: relaxed bar first, busy bar second.
            That's the <strong>2-3 direction</strong>. The notes are identical to the 3-2
            version — but starting on the calmer side gives the whole phrase a different
            arc. Songs that begin with anticipation or tension on the downbeat tend to want
            3-2; songs that resolve <em>into</em> the groove want 2-3.
          </p>
          <p>
            As a songwriter, this becomes a creative tool. Write a melody over the 3-2
            son and the lyric falls one way. Flip it to 2-3 and the same lyric breathes
            differently — the punchline lands on the upbeat half of the phrase rather than
            the downbeat half. Both are correct. They're just different stories.
          </p>
        </Prose>

        <TheoryBox label="The clave rule">
          Once a song has chosen a direction (3-2 or 2-3), it stays in that direction for
          the whole song. Flipping mid-song breaks the spell — Latin musicians call this a
          "crossed clave" and it's treated as a serious mistake.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="What's next" title="From rhythm back into harmony">
        <Prose>
          <p>
            That closes our run of rhythm chapters — tresillo, son, rumba, bossa. With
            these four patterns in your hand, you can sit at any keyboard and instantly play
            something that grooves. Your left hand holds the clave; your right hand floats
            anything diatonic on top.
          </p>
          <p>
            Next we return to harmony, and to the most important question of all: how chords
            are <em>built</em>. Every chord on every chart you'll ever read traces back to
            one of a handful of simple stacks. We'll start at the very bottom — two notes
            at a time — and work our way up.
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/lesson/music-theory-19"
        label="Chapter 19 · Chord Formation - Dyads & Triads"
        why="With rhythm settled, we return to harmony. Two-note dyads first, then three-note triads — the building blocks every chord on the planet stacks from."
      />
    </LessonShell>
  );
}
